import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CONTENT_ITEM, LANGUAGES } from 'config/types/prop';
import { options } from 'config/reactRedux';
import { setRichTextInnerHTML } from 'utils/setInnerHTML';
import updateImagesForLazyloading from 'utils/content';
import Thumbnail from 'components/thumbnail';
import withStyles from 'isomorphic-style-loader/withStyles';
import ContentList from 'components/contentList';
import ThemeProvider from 'components/hoc/themeProvider';
import CallToAction from 'components/callToAction';
import ArticleHeader from 'components/articleHeader';
import AuthorOverview from 'components/author/author-overview';
import I18n from 'components/i18n';
import CodeBlockSyntaxHighlighter from 'components/codeBlockSyntaxHighlighter';
import FeaturedVideo from 'components/video/featuredVideo';
import ShareButtons from 'components/shareButtons';
import StickyShareButtons from 'components/stickyShareButtons';
import sanitizeHtml from 'sanitize-html';
import { plainTextNoHTML } from 'config/sanitizeHTML';
import getPageTrackingData from 'selectors/getPageTrackingData';
import getIs404 from 'selectors/getIs404';
import useTrackPageCompleted from 'hooks/useTrackPageCompleted';
import { actionArticleVideoStarted } from 'actions';

import styles from '../contentItem.css';
import LoadingSpinner from '../../hoc/withLoader/loadingSpinner';

const DefaultContentItem = ({
  contentFiltered,
  item: {
    date,
    excerpt,
    title,
    metadata: {
      coauthors,
      featuredImageUrl,
      featuredVideoUrl,
      link,
      tracking: {
        ctaDesc,
        ctaLabel,
        goLink,
      },
    },
  },
  related: {
    relatedPosts,
    authorRelated,
  },
  currentEdition,
  contentContainerRef,
  pageTrackingData,
  isInProductMagazine,
  is404,
  onVideoClicked,
  isLoadingRelated,
}) => {
  const { data: trackingData } = pageTrackingData;
  const contentElement = useTrackPageCompleted({
    trackingData,
    threshold: 1,
    is404,
  });
  const author = coauthors[0]; // Always use the first coauthor
  const {
    relativePath,
    avatarUrl,
    displayName,
  } = author || {};

  const articleDescription = sanitizeHtml(excerpt, plainTextNoHTML);
  const articleTitleAndLink = `${title} ${link}`;
  // Checking if article content contains the code block
  const hasCodeBlock = contentFiltered.includes('wp-block-code');
  const hasImg = contentFiltered.includes('<img');
  const articleContent = hasImg ? updateImagesForLazyloading(contentFiltered) : contentFiltered;

  const shouldShowRecommendArticles = !isInProductMagazine;
  const hasRecommendedArticles = 0 < relatedPosts.length;

  return (
    <article className={styles.wrapper}>
      <section className={styles.main}>
        <ArticleHeader
          articleDate={date}
          articleAbsoluteLink={link}
          articleImage={featuredImageUrl}
          authorAvatar={avatarUrl}
          authorLink={relativePath}
          authorName={displayName}
          currentEdition={currentEdition}
          excerpt={excerpt}
          isArticlePage
          title={title}
          isInProductMagazine={isInProductMagazine}
        />
        <div className={styles.contentWrapper}>
          {!isInProductMagazine &&
            <StickyShareButtons
              currentEdition={currentEdition}
              shareProps={{
                description: articleDescription,
                image: featuredImageUrl,
                link,
                title,
                titleAndLink: articleTitleAndLink,
              }}
            />
          }
          <div className={styles.featuredAssetContainer}>
            {featuredImageUrl && !featuredVideoUrl ?
              <Thumbnail
                alt={title}
                originalSource={featuredImageUrl}
                position="featuredImage"
                wrapperClasses={styles.featuredImage}
              />
              :
              <FeaturedVideo
                title={title}
                url={featuredVideoUrl}
                onVideoClicked={onVideoClicked}
              />
            }
          </div>
          <div
            className={classNames('bodyCopy', styles.content)}
            dangerouslySetInnerHTML={setRichTextInnerHTML( // eslint-disable-line react/no-danger
              articleContent,
            )}
            ref={contentContainerRef}
          />
          <div ref={contentElement} />
          {goLink && ctaLabel &&
            <CallToAction
              isBig
              heading={ctaDesc}
              text={ctaLabel}
              url={goLink}
            />
          }
          {!isInProductMagazine &&
            <div className={styles.articleFooterLinks}>
              <span className={styles.label}>
                <I18n
                  field="share"
                  defaultText="Share this article"
                />
              </span>
              <ShareButtons
                currentEdition={currentEdition}
                shareProps={{
                  description: articleDescription,
                  image: featuredImageUrl,
                  link,
                  title,
                  titleAndLink: articleTitleAndLink,
                }}
              />
            </div>
          }
        </div>
        <footer>
          {author && <AuthorOverview
            articles={authorRelated}
            currentEdition={currentEdition}
            isInProductMagazine={isInProductMagazine}
            {...author}
          />}
          {shouldShowRecommendArticles &&
            hasRecommendedArticles ? (
              <div className={styles.recs}>
                <h2 className={styles.recsHeading}>
                  <I18n
                    field="recommended-articles"
                    defaultText="More Articles"
                  />
                </h2>
                <ThemeProvider themes={{
                  DefaultListViewItem: 'threeUp',
                  ContentList: 'threeUp',
                }}
                >
                  <ContentList items={relatedPosts} />
                </ThemeProvider>
              </div>
            ) : isLoadingRelated && <LoadingSpinner />
          }
        </footer>
      </section>
      {hasCodeBlock &&
        <CodeBlockSyntaxHighlighter articleContent={contentFiltered} />
      }
    </article>
  );
};

DefaultContentItem.propTypes = {
  contentFiltered: PropTypes.string.isRequired,
  item: PropTypes.shape(CONTENT_ITEM).isRequired,
  related: PropTypes.shape({
    authorRelated: PropTypes.arrayOf(
      PropTypes.shape(CONTENT_ITEM),
    ),
    relatedPosts: PropTypes.arrayOf(
      PropTypes.shape(CONTENT_ITEM),
    ),
  }).isRequired,
  currentEdition: LANGUAGES.isRequired,
  contentContainerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  pageTrackingData: PropTypes.object,
  isInProductMagazine: PropTypes.bool.isRequired,
  is404: PropTypes.bool.isRequired,
  onVideoClicked: PropTypes.func.isRequired,
  isLoadingRelated: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  is404: getIs404(state),
  pageTrackingData: getPageTrackingData(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  onVideoClicked: actionArticleVideoStarted,
}, dispatch);

const withRedux = connect(
  mapStateToProps,
  mapDispatchToProps,
  undefined,
  options,
);
const wrapWithStyles = withStyles(styles);

export default withRedux(wrapWithStyles(DefaultContentItem));
